import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Play, RefreshCw, Copy, CheckCircle, AlertCircle, Zap, Link, ExternalLink } from 'lucide-react';

const TRIGGER_COLORS = {
  nuevo_lead_peru:   '#3b82f6',
  lead_stage_changed:'#8b5cf6',
  lead_pagado:       '#22c55e',
  reporte_diario:    '#f59e0b',
  pago_recibido:     '#10b981',
  nuevo_usuario:     '#06b6d4',
  cita_agendada:     '#f97316',
  custom:            '#64748b',
};

export default function WebhooksHub({ authFetch }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [form, setForm]         = useState({ trigger: 'nuevo_lead_peru', webhook_url: '', label: '', workspace: 'neuralia' });
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(null);
  const [testResult, setTestResult] = useState({});
  const [copied, setCopied]     = useState('');
  const [activeTab, setActiveTab] = useState('outbound'); // outbound | inbound

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/webhooks/make');
      setData(res);
    } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const addWebhook = async () => {
    if (!form.webhook_url.trim()) return;
    setSaving(true);
    try {
      await authFetch('/api/admin/webhooks/make', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setForm({ trigger: 'nuevo_lead_peru', webhook_url: '', label: '', workspace: 'neuralia' });
      setAdding(false);
      await load();
    } finally { setSaving(false); }
  };

  const deleteWebhook = async (id) => {
    if (!window.confirm('¿Eliminar este webhook?')) return;
    await authFetch(`/api/admin/webhooks/make/${id}`, { method: 'DELETE' });
    await load();
  };

  const testWebhook = async (id) => {
    setTesting(id);
    try {
      const res = await authFetch(`/api/admin/webhooks/make/${id}/test`, { method: 'POST' });
      setTestResult((prev) => ({ ...prev, [id]: res }));
    } finally { setTesting(null); }
  };

  const generateInbound = async () => {
    const workspace = window.prompt('Nombre del workspace (ej: neuralia, cliente_lima, agencia_norte):', 'neuralia');
    if (!workspace) return;
    const label = window.prompt('Etiqueta para identificarlo:', workspace);
    const res = await authFetch('/api/admin/webhooks/make/inbound/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace, label: label || workspace }),
    });
    if (res.success) {
      copy(res.inbound_url, 'inbound_' + res.doc.id);
      alert(`✅ URL generada y copiada:\n\n${res.inbound_url}`);
      await load();
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-text-muted">
      <RefreshCw size={20} className="animate-spin mr-2" /> Cargando webhooks...
    </div>
  );

  const { webhooks = [], triggers = [], recent_logs = [], inbound_keys = [], inbound_base_url = '' } = data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-text-primary">Make.com · Webhook Hub</h2>
          <p className="text-sm text-text-muted">Conecta Neuralia369 con cualquier herramienta del mundo via Make.com</p>
        </div>
        <div className="flex gap-2">
          <a href="https://make.com" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle bg-card px-3 py-2 text-xs text-text-muted hover:border-primary/40 transition">
            make.com <ExternalLink size={11} />
          </a>
          <button onClick={load} className="rounded-xl border border-border-subtle bg-card px-3 py-2 hover:border-primary/40 transition">
            <RefreshCw size={14} className="text-text-muted" />
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
        <p className="text-sm font-bold text-primary mb-3">¿Cómo conectar con Make.com?</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2 text-xs text-text-muted">
            <p className="font-semibold text-text-primary">Neuralia → Make (Outbound)</p>
            <p>1. Ve a make.com → crea un escenario</p>
            <p>2. Agrega módulo <strong>"Webhooks → Custom Webhook"</strong></p>
            <p>3. Copia la URL que Make te da</p>
            <p>4. Pégala abajo con el trigger que quieres</p>
            <p>5. Cada vez que ocurra ese evento, Neuralia llama a Make</p>
          </div>
          <div className="space-y-2 text-xs text-text-muted">
            <p className="font-semibold text-text-primary">Make → Neuralia (Inbound)</p>
            <p>1. Genera una URL de inbound abajo</p>
            <p>2. En Make, usa <strong>"HTTP → Make a request"</strong></p>
            <p>3. Pega la URL + envía JSON con el campo <code className="text-cyan-300">action</code></p>
            <p>4. Acciones: <code className="text-cyan-300">crear_lead</code>, <code className="text-cyan-300">mover_etapa</code>, <code className="text-cyan-300">enviar_whatsapp</code>, <code className="text-cyan-300">reporte</code></p>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-border-subtle pb-2 flex-wrap">
        {[['outbound', `Outbound (${webhooks.length})`], ['inbound', `Inbound (${inbound_keys.length})`], ['escenarios', '3 Escenarios Clave'], ['logs', `Logs (${recent_logs.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`rounded-xl px-4 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-primary text-[#0A1A3F]' : 'text-text-muted hover:text-text-primary'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── ESCENARIOS MAKE.COM ── */}
      {activeTab === 'escenarios' && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
            <p className="text-sm font-bold text-primary mb-1">Los 3 escenarios que necesitas crear en Make.com</p>
            <p className="text-xs text-text-muted">Crea cada uno como un escenario nuevo en <a href="https://make.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">make.com</a>. Te llevará ~5 minutos cada uno.</p>
          </div>

          {/* Escenario 1 */}
          <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-white text-xs font-bold">1</span>
              <div>
                <p className="font-bold text-text-heading">Nuevo Lead → WhatsApp de bienvenida</p>
                <p className="text-xs text-text-muted">Se dispara cuando alguien llena el formulario /activa-peru</p>
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="rounded-xl border border-border-subtle bg-background/55 p-3">
                <p className="font-bold text-text-primary mb-2">Módulos en Make.com (en orden):</p>
                <div className="space-y-1 text-text-body">
                  <p>① <strong>Webhooks → Custom Webhook</strong> — crea el webhook, copia la URL</p>
                  <p>② Pega esa URL aquí (trigger: <code className="text-blue-300">nuevo_lead_peru</code>)</p>
                  <p>③ <strong>WATI → Send Template Message</strong> (o HTTP → Make a request)</p>
                  <p>   URL: <code className="text-blue-300">{'{{WATI_ENDPOINT}}'}/api/v1/sendSessionMessage/{'{{waId}}'}</code></p>
                  <p>   Parámetro: <code className="text-blue-300">messageText = "Hola {'{{nombre}}'}, gracias por contactar Neuralia369..."</code></p>
                  <p>④ <strong>MongoDB / HTTP</strong> → actualizar stage del lead a "respondio"</p>
                </div>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Datos que recibirás del webhook</p>
                <code className="text-xs text-text-body block">{'{ "lead": { "nombre": "...", "whatsapp": "51...", "empresa": "...", "sector": "...", "stage": "contactado" } }'}</code>
              </div>
            </div>
          </div>

          {/* Escenario 2 */}
          <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold">2</span>
              <div>
                <p className="font-bold text-text-heading">Lead Pagado → Onboarding automático</p>
                <p className="text-xs text-text-muted">Se dispara cuando un lead llega a la etapa "Pagado"</p>
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="rounded-xl border border-border-subtle bg-background/55 p-3">
                <p className="font-bold text-text-primary mb-2">Módulos en Make.com (en orden):</p>
                <div className="space-y-1 text-text-body">
                  <p>① <strong>Webhooks → Custom Webhook</strong> — trigger: <code className="text-green-300">lead_pagado</code></p>
                  <p>② <strong>WhatsApp / WATI</strong> → Mensaje de confirmación de pago</p>
                  <p>③ <strong>Gmail / Email</strong> → Enviar instrucciones de onboarding</p>
                  <p>④ <strong>Notion / Google Sheets</strong> → Crear ficha de cliente</p>
                  <p>⑤ <strong>Slack / Telegram</strong> → Notificar al equipo Neuralia369</p>
                </div>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Datos que recibirás del webhook</p>
                <code className="text-xs text-text-body block">{'{ "lead": { "nombre": "...", "whatsapp": "51...", "empresa": "...", "stage": "pagado", "plan_interes": "..." } }'}</code>
              </div>
            </div>
          </div>

          {/* Escenario 3 */}
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold">3</span>
              <div>
                <p className="font-bold text-text-heading">Reporte Diario → Email / Slack</p>
                <p className="text-xs text-text-muted">Se dispara automáticamente a las 18:30 hora Lima</p>
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="rounded-xl border border-border-subtle bg-background/55 p-3">
                <p className="font-bold text-text-primary mb-2">Módulos en Make.com (en orden):</p>
                <div className="space-y-1 text-text-body">
                  <p>① <strong>Webhooks → Custom Webhook</strong> — trigger: <code className="text-amber-300">reporte_diario</code></p>
                  <p>② <strong>Gmail</strong> → Enviar resumen a <code className="text-amber-300">juancarlos.vargas.on@gmail.com</code></p>
                  <p>   Asunto: <code className="text-amber-300">Reporte Neuralia369 — {'{{fecha}}'}</code></p>
                  <p>③ <strong>Slack / WhatsApp</strong> → Mensaje resumen al canal del equipo</p>
                </div>
              </div>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Datos que recibirás del webhook</p>
                <code className="text-xs text-text-body block">{'{ "report": { "total_leads": N, "nuevos_hoy": N, "pagados": N, "stages": {...} } }'}</code>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
            <p className="text-xs font-bold text-primary mb-2">Siguiente paso</p>
            <p className="text-xs text-text-muted">1. Crea los 3 escenarios en Make.com</p>
            <p className="text-xs text-text-muted">2. Copia cada URL de webhook de Make</p>
            <p className="text-xs text-text-muted">3. Ve a la pestaña <strong>Outbound</strong> y registra cada URL</p>
            <p className="text-xs text-text-muted">4. Activa los escenarios en Make.com</p>
          </div>
        </div>
      )}

      {/* ── OUTBOUND ── */}
      {activeTab === 'outbound' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-text-primary">Neuralia → Make.com</p>
            <button onClick={() => setAdding(!adding)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition">
              <Plus size={13} /> Agregar webhook
            </button>
          </div>

          {/* Formulario agregar */}
          {adding && (
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 space-y-3">
              <p className="text-xs font-bold text-text-primary">Nuevo webhook outbound</p>
              <div>
                <label className="text-xs text-text-muted block mb-1">Trigger (evento que lo dispara)</label>
                <select value={form.trigger} onChange={(e) => setForm((f) => ({ ...f, trigger: e.target.value }))}
                  className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs text-text-primary focus:border-primary focus:outline-none">
                  {triggers.map((t) => (
                    <option key={t.id} value={t.id}>{t.label} — {t.desc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-muted block mb-1">URL del webhook de Make.com</label>
                <input value={form.webhook_url} onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))}
                  placeholder="https://hook.eu2.make.com/xxxxxxxxxxxx"
                  className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs text-text-primary focus:border-primary focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Etiqueta (opcional)</label>
                  <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="Ej: TikTok publish, Slack notif..."
                    className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs text-text-primary focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Workspace</label>
                  <input value={form.workspace} onChange={(e) => setForm((f) => ({ ...f, workspace: e.target.value }))}
                    placeholder="neuralia"
                    className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs text-text-primary focus:border-primary focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setAdding(false)} className="rounded-xl border border-border-subtle px-4 py-2 text-xs text-text-muted hover:border-primary/40 transition">Cancelar</button>
                <button onClick={addWebhook} disabled={saving || !form.webhook_url.trim()}
                  className="rounded-xl bg-primary px-4 py-2 text-xs font-bold text-[#0A1A3F] disabled:opacity-50 hover:opacity-90 transition">
                  {saving ? 'Guardando...' : 'Guardar webhook'}
                </button>
              </div>
            </div>
          )}

          {/* Lista de webhooks */}
          <div className="space-y-3">
            {webhooks.map((hook) => {
              const triggerInfo = triggers.find((t) => t.id === hook.trigger) || {};
              const color = TRIGGER_COLORS[hook.trigger] || '#64748b';
              const tr = testResult[hook.id];
              return (
                <div key={hook.id} className="rounded-2xl border border-border-subtle bg-card p-4" data-testid={`make-webhook-${hook.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: color }}>
                          {triggerInfo.label || hook.trigger}
                        </span>
                        <span className="text-xs font-semibold text-text-primary">{hook.label}</span>
                        <span className="text-[10px] text-text-muted">· {hook.workspace}</span>
                      </div>
                      <p className="text-[11px] text-text-muted truncate">{hook.webhook_url}</p>
                      {tr && (
                        <div className={`mt-2 flex items-center gap-1 text-[11px] ${tr.success ? 'text-green-400' : 'text-red-400'}`}>
                          {tr.success ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                          {tr.success ? `✅ Éxito (${tr.status_code})` : `❌ Error: ${tr.error || tr.status_code}`}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => copy(hook.webhook_url, hook.id)}
                        className="rounded-xl border border-border-subtle p-2 text-text-muted hover:border-primary/40 transition" title="Copiar URL">
                        {copied === hook.id ? <CheckCircle size={13} className="text-green-400" /> : <Copy size={13} />}
                      </button>
                      <button onClick={() => testWebhook(hook.id)} disabled={testing === hook.id}
                        className="rounded-xl border border-border-subtle p-2 text-text-muted hover:border-cyan-400/40 hover:text-cyan-400 transition" title="Enviar prueba">
                        {testing === hook.id ? <RefreshCw size={13} className="animate-spin" /> : <Play size={13} />}
                      </button>
                      <button onClick={() => deleteWebhook(hook.id)}
                        className="rounded-xl border border-border-subtle p-2 text-text-muted hover:border-red-400/40 hover:text-red-400 transition" title="Eliminar">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {webhooks.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border-subtle p-8 text-center">
                <Zap size={24} className="text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted">Sin webhooks aún</p>
                <p className="text-xs text-text-muted mt-1">Agrega el primero para conectar Make.com con Neuralia</p>
              </div>
            )}
          </div>

          {/* Triggers disponibles */}
          <div className="rounded-2xl border border-border-subtle bg-card p-4">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Triggers disponibles</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {triggers.map((t) => (
                <div key={t.id} className="flex items-start gap-2 rounded-xl border border-border-subtle bg-background p-2.5">
                  <span className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: TRIGGER_COLORS[t.id] || '#64748b' }} />
                  <div>
                    <p className="text-[11px] font-bold text-text-primary">{t.label}</p>
                    <p className="text-[10px] text-text-muted">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── INBOUND ── */}
      {activeTab === 'inbound' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-text-primary">Make.com → Neuralia</p>
              <p className="text-xs text-text-muted">URLs únicas por workspace para recibir eventos de Make</p>
            </div>
            <button onClick={generateInbound}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition">
              <Plus size={13} /> Generar URL
            </button>
          </div>

          {inbound_keys.map((k) => {
            const url = `${inbound_base_url}${k.key}`;
            return (
              <div key={k.id} className="rounded-2xl border border-border-subtle bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-bold text-text-primary">{k.label}</p>
                    <p className="text-[10px] text-text-muted">Workspace: {k.workspace}</p>
                  </div>
                  <button onClick={() => copy(url, 'in_' + k.id)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle px-3 py-1.5 text-[11px] text-text-muted hover:border-primary/40 transition">
                    {copied === 'in_' + k.id ? <CheckCircle size={11} className="text-green-400" /> : <Copy size={11} />}
                    {copied === 'in_' + k.id ? 'Copiada' : 'Copiar URL'}
                  </button>
                </div>
                <code className="block rounded-xl bg-background border border-border-subtle px-3 py-2 text-[11px] text-cyan-300 break-all">{url}</code>
                <div className="mt-3 rounded-xl bg-background border border-border-subtle p-3">
                  <p className="text-[10px] font-bold text-text-muted mb-1.5">Payload de ejemplo (Make → Neuralia):</p>
                  <pre className="text-[10px] text-text-muted whitespace-pre-wrap">{JSON.stringify({ action: "crear_lead", nombre: "María García", empresa: "Distribuidora Lima", whatsapp: "+51999888777", sector: "Retail", mensaje: "Quiero saber más" }, null, 2)}</pre>
                </div>
              </div>
            );
          })}

          {inbound_keys.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border-subtle p-8 text-center">
              <Link size={24} className="text-text-muted mx-auto mb-2" />
              <p className="text-sm text-text-muted">Sin URLs inbound generadas</p>
              <p className="text-xs text-text-muted mt-1">Genera una URL para que Make.com pueda enviar datos a Neuralia</p>
            </div>
          )}
        </div>
      )}

      {/* ── LOGS ── */}
      {activeTab === 'logs' && (
        <div className="rounded-2xl border border-border-subtle bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-subtle bg-background">
                {['Trigger', 'Estado', 'Workspace', 'Hora'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-text-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_logs.map((log) => (
                <tr key={log.id} className="border-b border-border-subtle">
                  <td className="px-4 py-2.5 font-semibold text-text-primary">{log.trigger}</td>
                  <td className="px-4 py-2.5">
                    {log.ok
                      ? <span className="text-green-400 flex items-center gap-1"><CheckCircle size={11} /> {log.status}</span>
                      : <span className="text-red-400 flex items-center gap-1"><AlertCircle size={11} /> {log.status}</span>
                    }
                  </td>
                  <td className="px-4 py-2.5 text-text-muted">{log.workspace || '—'}</td>
                  <td className="px-4 py-2.5 text-text-muted">{log.fired_at ? new Date(log.fired_at).toLocaleString('es-PE') : '—'}</td>
                </tr>
              ))}
              {recent_logs.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-text-muted">Sin logs aún</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
